import type {
  BasketItem,
  Product,
  Variant,
  PublicShopConfig,
  BasketResponseData,
  ShopUser,
} from '@scayle/storefront-nuxt'
import {
  getTotalAppliedReductions,
  getFirstAttributeValue,
} from '@scayle/storefront-nuxt'
import useProductTrackingLocalStorage from '../useProductTrackingLocalStorage'
import useCheckoutTrackingLocalStorage from '../useCheckoutTrackingLocalStorage'
import type {
  BasketItemWithCoupon,
  CheckoutPaymentType,
  CheckoutShippingTier,
  CheckoutType,
  CustomerType,
  EcommerceItem,
  ProductListData,
  TrackingEvent,
  TrackingPayload
} from '~/types/tracking'
import {
  getSizeKey,
  getCategoryLevels,
} from '~/utils/tracking'
import useAgsUser from '~/composables/useAgsUser'
import { getSizeLabel } from '~/types/variant'

const collectBasketItems = (
  items: any[],
  options: { listName: string; listId: string },
) => {
  return items.map(
    (basketItem: any): ProductListData => ({
      product: basketItem.product,
      list: {
        name: options.listName,
        id: options.listId,
      },
      quantity: basketItem.quantity,
    }),
  )
}

const mapEcommerce = (
  basket: BasketResponseData,
  currency: PublicShopConfig['currency'],
  additional = {} as Record<string, unknown>,
) => {
  return {
    ...additional,
    currency,
    value: toFloat(basket.cost.withTax),
    net_value: toFloat(basket.cost.withoutTax),
    items: basket.items.map((item, index) => mapItem(item, index)),
  }
}

const mapItem = (
  basketItem: BasketItemWithCoupon,
  index: number,
): EcommerceItem => {
  const { product, variant, price, quantity } = basketItem

  const { getStoredProductTrackingItem } = useProductTrackingLocalStorage()
  const storedProductTracking = getStoredProductTrackingItem(String(product.referenceKey))

  return {
    item_id: product.id.toString(),
    item_sku: product.referenceKey || '',
    item_name: getFirstAttributeValue(product.attributes, 'name')?.label || '',
    item_brand: getFirstAttributeValue(product.attributes, 'brand')?.label || '',
    item_variant: getFirstAttributeValue(product.attributes, 'color')?.label || '',
    item_size: getSizeLabel(variant),
    item_size_key: variant.referenceKey
      ? getSizeKey(variant.referenceKey) || ''
      : '',
    item_category: getCategoryLevels(product.categories).item_category || '',
    item_category2: getCategoryLevels(product.categories).item_category2 || '',
    item_category3: getCategoryLevels(product.categories).item_category3 || '',
    price: toFloat(variant.price.withTax),
    discount: toFloat(getTotalAppliedReductions(price.unit).absoluteWithTax),
    coupon: basketItem.promotionCode || '',
    index,
    quantity,
    stock: variant.stock.quantity,
    ordertype: 'shop',
    item_list_name: storedProductTracking?.item_list_name || '',
    item_list_type: storedProductTracking?.item_list_type || '',
  }
}

const getCheckoutType = (user: ShopUser, customerType?: CustomerType): CheckoutType => {
  if (customerType === 'guest' || user.status?.isGuestCustomer) {
    return 'guest'
  }
  if (customerType === 'new') {
    return 'new_registration'
  }
  return 'existing_user'
}

const useCheckoutEvents = (
  track: (event: TrackingEvent, payload: TrackingPayload) => any
) => {
  const { currency: currencyCode } = useCurrentShop().value ?? {}
  const { getUserPayload } = useTrackingUserPayload()
  const { storeCheckoutTracking } = useCheckoutTrackingLocalStorage()

  return {
    trackCheckoutReached: async (basket: BasketResponseData) => {
      track('checkout_reached', {
        ecommerce: mapEcommerce(basket, currencyCode),
      })
    },
    trackBeginCheckout: async (
      basket?: BasketResponseData<Product, Variant>,
    ) => {
      const basketItems = basket?.items ?? []
      const basketKey = basket?.key ?? ''
      const { user, customerType } = useAgsUser()

      if (!user.value) {
        console.warn('User not found for checkout event')
        return
      }

      const checkout_type = getCheckoutType(user.value, customerType.value)

      track('begin_checkout', {
        user: await getUserPayload(),
        checkout_type,
        ecommerce: {
          currency: currencyCode,
          value: toFloat(basketItems.reduce((a, b) => a + b.price.total.withTax, 0)),
          net_value: toFloat(basketItems.reduce((a, b) => a + b.price.total.withoutTax, 0)),
          items: basketItems.map((basketItem, index) => mapItem(basketItem, index)),
        },
      })

      storeCheckoutTracking({
        [basketKey]: {
          checkout_type,
        },
      })
    },
    trackAddShippingInfo: (
      basketItems: BasketItem<Product, Variant>[] = [],
      shippingTier: CheckoutShippingTier,
    ) => {
      track('add_shipping_info', {
        ecommerce: {
          shipping_tier: shippingTier,
          currency: currencyCode,
          value: toFloat(basketItems.reduce((a, b) => a + b.price.total.withTax, 0)),
          net_value: toFloat(basketItems.reduce((a, b) => a + b.price.total.withoutTax, 0)),
          items: basketItems.map((basketItem, index) => mapItem(basketItem, index)),
        },
      })
    },
    trackAddPaymentInfo: (
      basketItems: BasketItem<Product, Variant>[] = [],
      paymentType: CheckoutPaymentType
    ) => {
      track('add_payment_info', {
        ecommerce: {
          payment_type: paymentType,
          currency: currencyCode,
          value: toFloat(basketItems.reduce((a, b) => a + b.price.total.withTax, 0)),
          net_value: toFloat(basketItems.reduce((a, b) => a + b.price.total.withoutTax, 0)),
          items: basketItems.map((basketItem, index) => mapItem(basketItem, index)),
        },
      })
    },
    collectBasketItems,
  }
}
export default useCheckoutEvents
